import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, Copy, Check } from 'lucide-react';

/** The WhatsApp glyph — lucide dropped brand marks, and this one has to be recognisable. */
function WhatsAppIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}

/**
 * Share affordance for the invitation.
 *
 * The URL is read from the address bar rather than configured, so it is always
 * whatever the visitor is actually looking at — no deploy-time setting to keep
 * in sync. On phones this hands off to the OS share sheet; everywhere else it
 * copies the link. WhatsApp gets its own button because that is how an
 * invitation like this actually travels.
 */
export default function ShareInvite({ message }) {
  const [url, setUrl] = useState('');
  const [canShare, setCanShare] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setUrl(window.location.href.split('#')[0]);
    setCanShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2400);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch (e) {
      // clipboard is blocked outside a secure context — fall back to a prompt
      window.prompt('Copy this link to share the invitation', url);
    }
  }, [url]);

  const share = useCallback(async () => {
    if (canShare) {
      try {
        await navigator.share({ title: 'Wedding Invitation', text: message, url });
        return;
      } catch (e) {
        // the sheet was dismissed, or sharing was refused — fall through to copy
      }
    }
    copy();
  }, [canShare, message, url, copy]);

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${message}\n${url}`)}`;

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex flex-wrap items-center justify-center gap-3">
        <motion.button
          type="button"
          onClick={share}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.96 }}
          className="glass gold-edge flex items-center gap-2.5 rounded-full px-6 py-3 font-sans text-xs uppercase tracking-[0.2em] text-maroon-700 transition-shadow hover:shadow-gold"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={copied ? 'done' : 'idle'}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.2 }}
              className="flex items-center"
            >
              {copied ? (
                <Check size={15} className="text-rose-500" />
              ) : canShare ? (
                <Share2 size={15} className="text-rose-500" />
              ) : (
                <Copy size={15} className="text-rose-500" />
              )}
            </motion.span>
          </AnimatePresence>
          {copied ? 'Link Copied' : canShare ? 'Share Invitation' : 'Copy Link'}
        </motion.button>

        <motion.a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.96 }}
          aria-label="Share the invitation on WhatsApp"
          className="glass gold-edge flex items-center gap-2.5 rounded-full px-6 py-3 font-sans text-xs uppercase tracking-[0.2em] text-maroon-700 transition-shadow hover:shadow-gold"
        >
          <span className="text-[#25D366]">
            <WhatsAppIcon size={16} />
          </span>
          WhatsApp
        </motion.a>
      </div>

      <p className="font-sans text-[11px] text-maroon-400">
        Send this invitation to someone you would love to see there.
      </p>
    </div>
  );
}
